/* © CNES */
import { transformExtent } from 'ol/proj';
import { filterSections } from '../../assets/config/filters/Referenciel';
import { START_DATE_TIME } from '../../components/shared/Constants';
import { GeoJsonPolygon } from '../../models/GeoJsonPolygon';



/**
 * Get Query Params for items requests from regards.
 * @param param each param to format for regards.
 * @param value each value of param that filter items accordingly.
 * @returns the query to interrogate regards items endpoint.
 */
export const getQueryParams = (param: string, value: any) => {
  const filter = Object.values(filterSections).find(
    (el) => el.param === param && el.param !== filterSections.dataProduct.param
  );
  switch (filter?.op) {
    case 'in':
      return {
        [param]: {
          in: [
            filterSections.whereAbsoluteOrbit.param,
            filterSections.whereRelativeOrbit.param,
          ].includes(filter.param)
            ? value.map(Number)
            : Array.isArray(value)
              ? value
              : [value],
        },
      };
    case 'between':
      return { [param]: { gte: parseInt(value[0]), lte: parseInt(value[1]) } };
    case 'lte':
      return { [param]: { lte: value } };
    case 'gte':
      return { [param]: { gte: value } };
    case 'contains':
      return { [param]: { contains: value } };
  }
};

/**
 * build Bbox Regards.
 * @param bbox number[] the bbox to format.
 * @returns Extent.
 */
export const buildBboxRegards = (bbox: number[]) => {
  return transformExtent(bbox, 'EPSG:3857', 'EPSG:4326');
};

/**
 * Build the Query to interrogate regards search items endpoint.
 * @param params all params that filter the result from search items.
 * @param page the pagination of results from items on regards side.
 * @param bbox the bbox of map to filter items on view.
 * @param intersects the intersects polygon.
 * @param selectedMissions the missions selected by user.
 * @returns the body to interrogate regards items endpoint.
 */
export const buildQueryParams = (
  params: Record<string, string[] | string | null>,
  bbox: number[] | undefined,
  page: number = 1,
  intersects: GeoJsonPolygon | null,
  selectedMissions?: number[] | null
) => {
  let argsObj: Record<string, any> = {};

  Object.keys(params).forEach((k: string) => {
    let value: string[] | string | null = params[k];


    // Transform L3B-OSO to L3B in processing:level for the query
    if (k === 'processing:level' && value && Array.isArray(value)) {
      value = value.map((level: string) => {
        if (level === 'L3B-OSO') {
          return 'L3B';
        }
        // Transform L2B-SNOW to L2B only for Sentinel-2 (mission 3), not for Landsat (mission 6)
        if (level === 'L2B-SNOW' && selectedMissions && selectedMissions.includes(3) && !selectedMissions.includes(6)) {
          return 'L2B';
        }
        return level;
      });
    }

    if (value && value.length > 0) {
      argsObj = { ...argsObj, ...getQueryParams(k, value) };
    }
  });

  // Add SNOW_MASK to productType specifically for L3B products
  if (params['processing:level'] && Array.isArray(params['processing:level']) && params['processing:level'].includes('L3B')) {
    const currentProductTypes = params['product:type'];
    if (!currentProductTypes || (Array.isArray(currentProductTypes) && !currentProductTypes.includes('SNOW_MASK'))) {
      const productTypeValues = Array.isArray(currentProductTypes)
        ? [...currentProductTypes, 'SNOW_MASK']
        : ['SNOW_MASK'];
      argsObj = {
        ...argsObj,
        'product:type': { in: productTypeValues }
      };
    }
  }

  return {
    sortBy: [{ direction: 'desc', field: START_DATE_TIME }],
    limit: 20,
    page: page,
    query: { ...argsObj, latest: { eq: true } },
    ...(bbox && { bbox: buildBboxRegards(bbox) }),
    ...(intersects && { intersects: intersects.features[0].geometry }),
  };
};
